import { isTypingTarget, type IssueGroupBy, type IssueOrder } from '@/lib/linear-utils';
import {
  cyclesPath,
  documentsPath,
  inboxPath,
  issueAssigneePath,
  issuePriorityPath,
  issuesPath,
  myIssuesPath,
  openIssuePath,
  projectsPath,
  reviewsPath,
  settingsPath,
  teamsPath,
} from '@/lib/linear-paths';
import type { Issue } from '@/types/issue';
import type { SharedPageProps } from '@/types/shared';
import { router, usePage } from '@inertiajs/react';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';

const LAYOUT_KEY = 'caprock:issue-display';

export type ShortcutMenuKind = 'status' | 'priority' | 'assignee' | 'label' | null;
export type IssueLayout = 'list' | 'board' | 'timeline';

interface DisplayState {
  layout: IssueLayout;
  grouping: IssueGroupBy;
  order: IssueOrder;
}

function readDisplay(): DisplayState {
  try {
    const raw = localStorage.getItem(LAYOUT_KEY);
    if (!raw) return { layout: 'list', grouping: 'status', order: 'updated' };
    const parsed = JSON.parse(raw) as Partial<DisplayState>;
    const layout =
      parsed.layout === 'board' || parsed.layout === 'timeline' ? parsed.layout : 'list';
    const grouping =
      parsed.grouping === 'assignee' ||
      parsed.grouping === 'priority' ||
      parsed.grouping === 'project' ||
      parsed.grouping === 'none'
        ? parsed.grouping
        : 'status';
    const order =
      parsed.order === 'created' || parsed.order === 'priority' || parsed.order === 'title'
        ? parsed.order
        : 'updated';
    return { layout, grouping, order };
  } catch {
    return { layout: 'list', grouping: 'status', order: 'updated' };
  }
}

function writeDisplay(state: DisplayState) {
  localStorage.setItem(LAYOUT_KEY, JSON.stringify(state));
}

export interface SelectableItem {
  id: string;
  href?: string;
  identifier?: string;
  issue?: Issue;
}

interface WorkspaceChromeValue {
  paletteOpen: boolean;
  setPaletteOpen: (open: boolean) => void;
  createIssueOpen: boolean;
  setCreateIssueOpen: (open: boolean) => void;
  selectedIndex: number;
  setSelectedIndex: (index: number) => void;
  items: SelectableItem[];
  registerItems: (next: SelectableItem[]) => void;
  peekIssue: Issue | null;
  setPeekIssue: (issue: Issue | null) => void;
  shortcutMenu: ShortcutMenuKind;
  setShortcutMenu: (menu: ShortcutMenuKind) => void;
  selectedIssue: Issue | null;
  layout: IssueLayout;
  setLayout: (layout: IssueLayout) => void;
  grouping: IssueGroupBy;
  setGrouping: (grouping: IssueGroupBy) => void;
  order: IssueOrder;
  setOrder: (order: IssueOrder) => void;
  shortcutsOpen: boolean;
  setShortcutsOpen: (open: boolean) => void;
}

const WorkspaceChromeContext = createContext<WorkspaceChromeValue | null>(null);

export function useWorkspaceChrome(): WorkspaceChromeValue {
  const value = useContext(WorkspaceChromeContext);
  if (!value) {
    throw new Error('useWorkspaceChrome must be used within WorkspaceChromeProvider');
  }
  return value;
}

interface ProviderProps {
  children: ReactNode;
}

export default function WorkspaceChromeProvider({ children }: ProviderProps) {
  const { currentUser } = usePage<SharedPageProps>().props;
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [createIssueOpen, setCreateIssueOpen] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [items, setItems] = useState<SelectableItem[]>([]);
  const [peekIssue, setPeekIssue] = useState<Issue | null>(null);
  const [shortcutMenu, setShortcutMenu] = useState<ShortcutMenuKind>(null);
  const [shortcutsOpen, setShortcutsOpen] = useState(false);
  const [display, setDisplay] = useState<DisplayState>(() =>
    typeof window === 'undefined' ? { layout: 'list', grouping: 'status', order: 'updated' } : readDisplay()
  );
  const goChord = useRef(false);
  const goTimer = useRef<number | null>(null);

  const selectedIssue = items[selectedIndex]?.issue ?? peekIssue;
  const layout = display.layout;
  const grouping = display.grouping;
  const order = display.order;

  const persistDisplay = useCallback((next: DisplayState) => {
    setDisplay(next);
    writeDisplay(next);
  }, []);

  const setLayout = useCallback(
    (next: IssueLayout) => persistDisplay({ ...display, layout: next }),
    [display, persistDisplay]
  );
  const setGrouping = useCallback(
    (next: IssueGroupBy) => persistDisplay({ ...display, grouping: next }),
    [display, persistDisplay]
  );
  const setOrder = useCallback(
    (next: IssueOrder) => persistDisplay({ ...display, order: next }),
    [display, persistDisplay]
  );

  const registerItems = useCallback((next: SelectableItem[]) => {
    setItems(next);
    setSelectedIndex((current) => {
      if (next.length === 0) return -1;
      if (current < 0) return 0;
      if (current >= next.length) return next.length - 1;
      return current;
    });
  }, []);

  const armGo = useCallback(() => {
    goChord.current = true;
    if (goTimer.current) window.clearTimeout(goTimer.current);
    goTimer.current = window.setTimeout(() => {
      goChord.current = false;
    }, 800);
  }, []);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      const typing = isTypingTarget(event.target);
      const key = event.key.toLowerCase();

      if ((event.metaKey || event.ctrlKey) && key === 'k') {
        event.preventDefault();
        setPaletteOpen(true);
        return;
      }

      if ((event.metaKey || event.ctrlKey) && key === 'b') {
        event.preventDefault();
        setLayout(layout === 'board' ? 'list' : 'board');
        return;
      }

      if ((event.metaKey || event.ctrlKey) && event.shiftKey && event.key === '.') {
        event.preventDefault();
        if (selectedIssue?.identifier) {
          navigator.clipboard.writeText(selectedIssue.identifier).catch(() => undefined);
        }
        return;
      }

      if (event.key === 'Escape') {
        if (shortcutsOpen) {
          setShortcutsOpen(false);
          return;
        }
        if (shortcutMenu) {
          setShortcutMenu(null);
          return;
        }
        if (peekIssue) {
          setPeekIssue(null);
          return;
        }
        if (paletteOpen) {
          setPaletteOpen(false);
          return;
        }
        if (createIssueOpen) {
          setCreateIssueOpen(false);
        }
        return;
      }

      if (typing) return;

      if (event.key === '?' || (event.shiftKey && event.key === '/')) {
        event.preventDefault();
        setShortcutsOpen((open) => !open);
        return;
      }

      if (key === '/' && !event.metaKey && !event.ctrlKey) {
        event.preventDefault();
        setPaletteOpen(true);
        return;
      }

      if (goChord.current) {
        event.preventDefault();
        goChord.current = false;
        if (key === 'i') router.visit(inboxPath());
        if (key === 'm') router.visit(myIssuesPath());
        if (key === 'p') router.visit(projectsPath());
        if (key === 's') router.visit(settingsPath());
        if (key === 't') router.visit(teamsPath());
        if (key === 'c') router.visit(cyclesPath());
        if (key === 'a') router.visit(`${issuesPath()}?view=active`);
        if (key === 'b') router.visit(`${issuesPath()}?view=backlog`);
        if (key === 'e') router.visit(issuesPath());
        if (key === 'r') router.visit(reviewsPath());
        if (key === 'd') router.visit(documentsPath());
        if (key === 'v') router.visit('/app/views');
        return;
      }

      if (key === 'g' && !event.metaKey && !event.ctrlKey && !event.altKey) {
        event.preventDefault();
        armGo();
        return;
      }

      if (key === 'c' && !event.metaKey && !event.ctrlKey && !event.altKey) {
        event.preventDefault();
        setCreateIssueOpen(true);
        return;
      }

      if (selectedIssue) {
        if (key === 's' && !event.shiftKey) {
          event.preventDefault();
          setShortcutMenu('status');
          return;
        }
        if (key === 'p' && !event.shiftKey) {
          event.preventDefault();
          setShortcutMenu('priority');
          return;
        }
        if (key === 'a' && !event.shiftKey) {
          event.preventDefault();
          setShortcutMenu('assignee');
          return;
        }
        if (key === 'l' && !event.shiftKey) {
          event.preventDefault();
          setShortcutMenu('label');
          return;
        }
        if (key === 'i' && !event.shiftKey) {
          event.preventDefault();
          if (currentUser?.id) {
            router.patch(
              issueAssigneePath(selectedIssue.id),
              { assigneeId: currentUser.id },
              { preserveScroll: true }
            );
          }
          return;
        }
        if (event.shiftKey && ['1', '2', '3', '4', '0'].includes(event.key)) {
          event.preventDefault();
          const map: Record<string, number> = { '1': 1, '2': 2, '3': 3, '4': 4, '0': 0 };
          router.patch(
            issuePriorityPath(selectedIssue.id),
            { priority: map[event.key] },
            { preserveScroll: true }
          );
          return;
        }
      }

      if (key === 'j' || event.key === 'ArrowDown') {
        event.preventDefault();
        setSelectedIndex((current) => {
          if (items.length === 0) return -1;
          const next = current + 1;
          return next >= items.length ? 0 : next;
        });
        return;
      }

      if (key === 'k' || event.key === 'ArrowUp') {
        event.preventDefault();
        setSelectedIndex((current) => {
          if (items.length === 0) return -1;
          const next = current - 1;
          return next < 0 ? items.length - 1 : next;
        });
        return;
      }

      if (event.key === ' ' && selectedIndex >= 0 && items[selectedIndex]?.issue) {
        event.preventDefault();
        setPeekIssue(items[selectedIndex].issue ?? null);
        return;
      }

      if (event.key === 'Enter' && selectedIndex >= 0 && items[selectedIndex]) {
        event.preventDefault();
        const item = items[selectedIndex];
        if (item.issue) {
          router.visit(openIssuePath(item.issue));
          return;
        }
        const href = item.href ?? openIssuePath({ id: item.id, identifier: item.identifier });
        router.visit(href);
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [
    armGo,
    createIssueOpen,
    currentUser?.id,
    items,
    layout,
    paletteOpen,
    peekIssue,
    selectedIndex,
    selectedIssue,
    setLayout,
    shortcutMenu,
    shortcutsOpen,
  ]);

  const value = useMemo(
    () => ({
      paletteOpen,
      setPaletteOpen,
      createIssueOpen,
      setCreateIssueOpen,
      selectedIndex,
      setSelectedIndex,
      items,
      registerItems,
      peekIssue,
      setPeekIssue,
      shortcutMenu,
      setShortcutMenu,
      selectedIssue,
      layout,
      setLayout,
      grouping,
      setGrouping,
      order,
      setOrder,
      shortcutsOpen,
      setShortcutsOpen,
    }),
    [
      createIssueOpen,
      grouping,
      items,
      layout,
      order,
      paletteOpen,
      peekIssue,
      registerItems,
      selectedIndex,
      selectedIssue,
      setGrouping,
      setLayout,
      setOrder,
      shortcutMenu,
      shortcutsOpen,
    ]
  );

  return (
    <WorkspaceChromeContext.Provider value={value}>{children}</WorkspaceChromeContext.Provider>
  );
}
